/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `UploadProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface UploadProps {
    /**
     * A string representing the CSS class to be applied to the UploadIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#fff'
     */
    color1?: string;
    /**
     * A number or string representing the height of the UploadIcon element.
     *
     * @default 26
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Upload'
     */
    testId?: string;
    /**
     * A number or string representing the height of the UploadIcon element.
     *
     * @default 26
     */
    width?: number | string;
}

/**
 * The `Upload` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `UploadProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the UploadIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the UploadIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the UploadIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const UploadComponent = <Upload className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Upload = ({
    className = '',
    color1 = '#fff',
    height = 26,
    ref,
    testId = 'Upload',
    width = 26
}: UploadProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 26 26"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M22.75 16.25v4.33a2.17 2.17 0 0 1-2.17 2.17H5.42a2.17 2.17 0 0 1-2.17-2.17v-4.33m15.17-7.58L13 3.25 7.58 8.67M13 3.25v13" fill="transparent" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.17" />
    </svg>
);

Upload.displayName = 'Upload';